
import { Component, Vue } from "vue-property-decorator";
import { actions, Equipment, EquipmentType, Facility, PunchItem } from "@/survey";
import FacilityStatusBar from "@/views/charts/FacilityStatusBar.vue";
import FacilityPriorityBar from "@/views/charts/FacilityPriorityBar.vue";
import FacilitySizePie from "@/views/charts/FacilitySizePie.vue";
import TagUsagePie from "@/views/charts/TagUsagePie.vue";

type BreakdownRow = { label: string; count: number; percent: number };
type FacilityCountRow = { facility: string; total: number };
type QAFacilityRow = { facility: string; total: number; open: number; completed: number };

@Component({ components: { FacilityStatusBar, FacilityPriorityBar, TagUsagePie, FacilitySizePie } })
export default class ProjectDashboard extends Vue {
  equipmentFacilityFields = [
    { key: "facility", label: "Facility", sortable: true },
    { key: "total", label: "Equipment", sortable: true }
  ];

  mounted() {
    this.refreshDashboard();
  }

  refreshDashboard() {
    this.$store.dispatch(actions.GET_FACILITIES);
    this.$store.dispatch(actions.GET_EQUIPMENT);
    this.$store.dispatch(actions.GET_TAG_USAGE);
    this.$store.dispatch(actions.GET_PUNCH_ITEMS).catch(() => undefined);
  }

  get facilities(): Facility[] { return this.$store.state.facilities; }
  get equipment(): Equipment[] { return this.$store.state.equipment; }
  get punchItems(): PunchItem[] { return this.$store.state.punchItems; }
  get completedFacilities(): Facility[] { return this.facilities.filter((facility: Facility) => facility.status === "completed"); }
  get qaFacilities(): Facility[] { return this.facilities.filter((facility: Facility) => facility.qa === true); }
  get qaCompleteFacilities(): Facility[] { return this.qaFacilities.filter((facility: Facility) => facility.qaCompleted === true); }
  get surveyedEquipment(): Equipment[] { return this.equipment.filter((equipment: Equipment) => equipment.surveyed === true); }
  get unsurveyedEquipment(): Equipment[] { return this.equipment.filter((equipment: Equipment) => equipment.surveyed !== true); }
  get equipmentMissingPhoto(): Equipment[] { return this.equipment.filter((equipment: Equipment) => !equipment.attachmentMeta || equipment.attachmentMeta.length === 0); }
  get equipmentMissingLocation(): Equipment[] { return this.equipment.filter((equipment: Equipment) => !equipment.lat || !equipment.lng); }
  get completedPunchItems(): PunchItem[] { return this.punchItems.filter((item: PunchItem) => Boolean(item.completed)); }
  get openPunchItems(): PunchItem[] { return this.punchItems.filter((item: PunchItem) => !item.completed); }

  get completionPercent() { return this.percentOf(this.completedFacilities.length, this.facilities.length); }
  get equipmentSurveyPercent() { return this.percentOf(this.surveyedEquipment.length, this.equipment.length); }
  get qaCompletionPercent() { return this.percentOf(this.qaCompleteFacilities.length, this.qaFacilities.length); }
  get totalAttentionItems() { return this.unsurveyedEquipment.length + this.equipmentMissingPhoto.length + this.equipmentMissingLocation.length + this.openPunchItems.length; }

  get metrics() {
    return [
      { label: "Facilities", value: this.facilities.length, detail: `${this.completedFacilities.length} complete`, icon: "building", variant: "blue" },
      { label: "Equipment", value: this.equipment.length, detail: `${this.equipmentSurveyPercent}% surveyed`, icon: "hdd-network", variant: "slate" },
      { label: "QA Facilities", value: this.qaFacilities.length, detail: `${this.qaCompletionPercent}% complete`, icon: "clipboard-check", variant: "green" },
      { label: "Open QAQC", value: this.openPunchItems.length, detail: `${this.completedPunchItems.length} closed`, icon: "flag", variant: "red" }
    ];
  }

  get facilityStatusRows(): BreakdownRow[] { return this.breakdown(this.facilities, (facility: Facility) => this.cleanLabel(facility.status, "No status")); }
  get facilityPriorityRows(): BreakdownRow[] { return this.breakdown(this.facilities, (facility: Facility) => this.cleanLabel(facility.priority, "No priority")); }
  get facilityRegionRows(): BreakdownRow[] { return this.breakdown(this.facilities, (facility: Facility) => this.cleanLabel(facility.region, "No region")); }
  get equipmentTypeRows(): BreakdownRow[] { return this.breakdown(this.equipment, (equipment: Equipment) => this.equipmentTypeName(equipment.equipmentType)); }
  get equipmentFacilityRows(): FacilityCountRow[] { return this.sortedFacilityCounts(this.equipment.map((equipment: Equipment) => equipment.facility)).slice(0, 10); }

  get qAFacilityRows(): QAFacilityRow[] {
    const rows = this.facilities.map((facility: Facility) => {
      const items = this.punchItems.filter((item: PunchItem) => item.facility === facility.id);
      return {
        facility: this.facilityName(facility.id),
        total: items.length,
        open: items.filter((item: PunchItem) => !item.completed).length,
        completed: items.filter((item: PunchItem) => Boolean(item.completed)).length
      };
    });
    return rows.filter((row: QAFacilityRow) => row.total > 0).sort((left: QAFacilityRow, right: QAFacilityRow) => right.open - left.open || right.total - left.total).slice(0, 8);
  }

  percentOf(count: number, total: number) {
    return total === 0 ? 0 : Math.round((count / total) * 100);
  }

  cleanLabel(label: string | undefined | null, fallback: string) {
    return label === undefined || label === null || `${label}`.trim() === "" ? fallback : `${label}`;
  }

  breakdown<T>(items: T[], labeler: (item: T) => string): BreakdownRow[] {
    const counts: { [key: string]: number } = {};
    items.forEach((item: T) => {
      const label = labeler(item);
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.keys(counts)
      .map((label: string) => ({ label, count: counts[label], percent: this.percentOf(counts[label], items.length) }))
      .sort((left: BreakdownRow, right: BreakdownRow) => right.count - left.count || left.label.localeCompare(right.label))
      .slice(0, 8);
  }

  sortedFacilityCounts(facilityIds: number[]): FacilityCountRow[] {
    const counts: { [key: string]: number } = {};
    facilityIds.forEach((facilityId: number) => {
      const label = this.facilityName(facilityId);
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.keys(counts).map((facility: string) => ({ facility, total: counts[facility] })).sort((left: FacilityCountRow, right: FacilityCountRow) => right.total - left.total || left.facility.localeCompare(right.facility));
  }

  facilityName(facilityId: number | null | undefined) {
    const facility = this.facilities.find((item: Facility) => item.id === facilityId);
    if (facility === undefined) {
      return `Facility ${facilityId || "unknown"}`;
    }
    return facility.facilityCode ? `${facility.facilityCode} - ${facility.name}` : facility.name;
  }

  equipmentTypeName(equipmentTypeId: number) {
    const equipmentType = this.$store.state.equipmentTypes.find((item: EquipmentType) => item.id === equipmentTypeId);
    return equipmentType === undefined ? "Unknown type" : equipmentType.name;
  }
}
